import {FamilyGoalType} from "./models/FamilyGoalType";
import {GoalModelType} from "./models/GoalModelType";
import {NonLifestyleGoalType} from "./models/GoalType";
import {
    AlignedLifestylePresentValueRequest,
    AlignedNonLifestyleGoalRequest,
} from "./models/PresentValue";
import {LifestyleSpendingGoal} from "./models/LifestyleSpendingGoal";

type AlignableGoal = Pick<NonLifestyleGoalType, "id" | "createdTimestamp" | "prioritizationType" | "calculatedFields"> & {
    userInputs: Pick<NonLifestyleGoalType["userInputs"],
        "annualFlow" |
        "startDate" |
        "endDate" |
        "riskControl" |
        "isFundedByNonInvestableAssets" |
        "description" |
        "isAssetPurchase"
    >;
};

const buildAlignedGoalRequest = (
    goal: AlignableGoal,
    nonPortfolioFunded: number,
    isAssetPurchase: boolean,
): AlignedNonLifestyleGoalRequest => {
    const {annualFlow, startDate, endDate, riskControl, isFundedByNonInvestableAssets, description} = goal.userInputs;

    return {
        goalId: goal.id ?? "",
        annualFlow,
        startDate,
        endDate,
        riskControlAsset: riskControl ?? undefined,
        isFundedByNonInvestableAssets,
        nonPortfolioFunded,
        isAssetPurchase,
        description,
        modifiedDate: goal.calculatedFields.modifiedDate ?? goal.createdTimestamp,
        prioritizationType: goal.prioritizationType,
    };
};

export const buildAlignedNonLifestyleRequest = (goal: NonLifestyleGoalType): AlignedNonLifestyleGoalRequest => {
    return buildAlignedGoalRequest(goal, 0, goal.userInputs.isAssetPurchase ?? false);
};

export const buildAlignedFamilyGoalRequest = (goal: FamilyGoalType): AlignedNonLifestyleGoalRequest => {
    return buildAlignedGoalRequest(
        {...goal, userInputs: {...goal.userInputs, isAssetPurchase: false}},
        goal.familyAssetsAmount ?? 0,
        false,
    );
};

export const flattenFamilyGoals = (familyGoals: GoalModelType["familyGoals"]): FamilyGoalType[] => {
    return Object.values(familyGoals).flatMap((beneficiary) => Object.values(beneficiary.goals));
};

export const buildAlignedLifestylePresentValueRequest = (
    lifestyle: LifestyleSpendingGoal["userInputs"],
    nonLifestyleGoals: NonLifestyleGoalType[] = [],
    familyGoals: FamilyGoalType[] = [],
): AlignedLifestylePresentValueRequest => {
    return {
        lifestyle,
        nonLifestyle: [
            ...nonLifestyleGoals.map(buildAlignedNonLifestyleRequest),
            ...familyGoals.map(buildAlignedFamilyGoalRequest),
        ],
    };
};

export const buildAlignedLifestylePresentValueRequestFromGoalModel = (
    lifestyle: LifestyleSpendingGoal["userInputs"],
    goalModel: Pick<GoalModelType, "nonLifestyleGoals" | "familyGoals">,
): AlignedLifestylePresentValueRequest => {
    return buildAlignedLifestylePresentValueRequest(
        lifestyle,
        goalModel.nonLifestyleGoals,
        flattenFamilyGoals(goalModel.familyGoals),
    );
};


